import React, { useRef } from 'react';
import './Header.scss';
import { AppWrap } from '../../wrapper';
import images from '../../constants/images';
import { motion, useMotionValue, useSpring, useReducedMotion } from 'framer-motion';

const techStack = [
	{ icon: images.WP, label: 'WordPress' },
	{ icon: images.Shopify, label: 'Shopify' },
	{ icon: images.Nextjs, label: 'Next.js' },
	{ icon: images.JS, label: 'JavaScript' },
	{ icon: images.Astro, label: 'Astro' },
];

const marqueeItems = [
	'Fullstack Development',
	'React.js',
	'Next.js',
	'Shopify Development',
	'SEO',
	'GEO',
	'Frontend Engineering',
	'UX Engineering',
	'WordPress',
	'Freelance',
];

const copyStagger = {
	hidden: {},
	visible: { transition: { staggerChildren: 0.12, delayChildren: 0.2 } },
};

const fadeUp = {
	hidden: { y: 40, opacity: 0 },
	visible: { y: 0, opacity: 1, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};

const lineReveal = {
	hidden: { y: '110%' },
	visible: { y: '0%', transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

const Header = () => {
	const reduceMotion = useReducedMotion();
	const visualRef = useRef(null);

	const rx = useMotionValue(0);
	const ry = useMotionValue(0);
	const rotateX = useSpring(rx, { stiffness: 120, damping: 18 });
	const rotateY = useSpring(ry, { stiffness: 120, damping: 18 });

	const handleMouseMove = (e) => {
		if (reduceMotion || !visualRef.current) return;
		const rect = visualRef.current.getBoundingClientRect();
		const px = (e.clientX - rect.left) / rect.width - 0.5;
		const py = (e.clientY - rect.top) / rect.height - 0.5;
		ry.set(px * 16);
		rx.set(-py * 12);
	};

	const handleMouseLeave = () => {
		rx.set(0);
		ry.set(0);
	};

	return (
		<div className='hero'>
			{/* <div className='hero-glow hero-glow--pink' aria-hidden='true' /> */}
			{/* <div className='hero-glow hero-glow--purple' aria-hidden='true' /> */}

			<div className='hero-grid'>
				<motion.div
					className='hero-copy'
					variants={copyStagger}
					initial='hidden'
					animate='visible'
				>
					<motion.p className='eyebrow hero-eyebrow' variants={fadeUp}>
						<span className='pulse-dot' aria-hidden='true' />
						Available for new projects
					</motion.p>

					<h1 className='hero-title'>
						<span className='line-mask'>
							<motion.span className='line text-gradient' variants={lineReveal}>
								Fullstack
							</motion.span>
						</span>
						<span className='line-mask'>
							<motion.span className='line line--outline' variants={lineReveal}>
								Developer
							</motion.span>
						</span>
					</h1>

					<motion.p className='hero-sub' variants={fadeUp}>
						I'm <strong>Abdul Rafay</strong> — I craft <em>immersive</em>, high-performance
						web experiences that turn bold ideas into pixel-perfect products.
					</motion.p>

					<motion.div className='hero-cta' variants={fadeUp}>
						<a className='btn btn-gradient' href='#work'>
							View My Work
						</a>
						<a className='btn btn-ghost' href='#contact'>
							Let's Talk
						</a>
					</motion.div>

					<motion.div className='hero-tech' variants={fadeUp}>
						{techStack.map((tech, i) => (
							<motion.div
								className='tech-chip'
								key={tech.label}
								title={tech.label}
								animate={reduceMotion ? undefined : { y: [0, -9, 0] }}
								transition={{
									duration: 3.6 + i * 0.5,
									repeat: Infinity,
									ease: 'easeInOut',
									delay: i * 0.3,
								}}
							>
								<img src={tech.icon} alt={tech.label} />
							</motion.div>
						))}
					</motion.div>
				</motion.div>

				<div
					className='hero-visual'
					ref={visualRef}
					onMouseMove={handleMouseMove}
					onMouseLeave={handleMouseLeave}
				>
					<motion.div
						className='hero-card'
						style={{ rotateX, rotateY }}
						initial={{ opacity: 0, y: 60, scale: 0.94 }}
						animate={{ opacity: 1, y: 0, scale: 1 }}
						transition={{ duration: 1, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
					>
						<img src={images.main} alt='Abdul Rafay' />
						<div className='hero-card-overlay' aria-hidden='true' />
						<div className='hero-card-frame' aria-hidden='true' />
						<div className='hero-card-tag'>
							<span className='tag-name'>Abdul Rafay</span>
							<span className='tag-role'>Wordpress & Shopify Specialist</span>
						</div>
					</motion.div>
				</div>
			</div>

			<a className='hero-scroll' href='#about'>
				Scroll
				<span className='hero-scroll-line' aria-hidden='true' />
			</a>

			<div className='hero-marquee' aria-hidden='true'>
				<div className='marquee-track'>
					{[...marqueeItems, ...marqueeItems].map((item, i) => (
						<span className='marquee-item' key={i}>
							{item}
							<span className='marquee-star'>✦</span>
						</span>
					))}
				</div>
			</div>
		</div>
	);
};

export default AppWrap(Header, 'home');
